import Chart from "react-apexcharts";
import type { ApexOptions } from "apexcharts";
import { useState, useEffect, useRef } from "react";
import { HiArrowRight } from "react-icons/hi";
import { Button } from "flowbite-react";
import { useTranslation } from "@repo/core";
import { useCorelationMatrix, type Strategy } from "@repo/api";

interface StrategiesCorrelationMatrixProps {
  strategies: Strategy[];
  className?: string;
}

const StrategiesCorrelationMatrix = ({
  strategies,
  className = "",
}: StrategiesCorrelationMatrixProps) => {
  const [showScrollOverlay, setShowScrollOverlay] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Fetch real correlation data from backend
  const { data: correlationMatrix, isLoading } = useCorelationMatrix();

  // Debug logging
  useEffect(() => {
    if (correlationMatrix) {
      console.log('Correlation Matrix Data:', correlationMatrix);
      console.log('Number of strategies with correlation data:', Object.keys(correlationMatrix).length);
      console.log('Number of strategies passed to component:', strategies.length);
    }
  }, [correlationMatrix, strategies.length]);

  // Check if correlation data is complete
  const isCorrelationDataComplete = () => {
    if (!correlationMatrix) return false;

    const matrixStrategyIds = Object.keys(correlationMatrix);
    const componentStrategyIds = strategies.map(s => s.modelId);

    // Check if all strategies in the component have correlation data
    const allStrategiesHaveData = componentStrategyIds.every(id =>
      matrixStrategyIds.includes(id)
    );

    return allStrategiesHaveData;
  };

  // Check if content has scrollbar and show overlay
  useEffect(() => {
    const checkForScrollbar = () => {
      if (scrollContainerRef.current) {
        const hasHorizontalScrollbar =
          scrollContainerRef.current.scrollWidth >
          scrollContainerRef.current.clientWidth;

        if (hasHorizontalScrollbar && strategies.length > 0) {
          setShowScrollOverlay(true);
        }
      }
    };

    // Check for scrollbar after a short delay to ensure content is rendered
    const timer = setTimeout(checkForScrollbar, 500);

    return () => clearTimeout(timer);
  }, [strategies]);

  // Get correlation value from real backend data
  const getCorrelation = (strategy1: Strategy, strategy2: Strategy): number => {
    if (strategy1.modelId === strategy2.modelId) return 1.0;

    // Check if correlation data is available
    if (!correlationMatrix) return 0;

    // Look up correlation in the matrix
    const correlation =
      correlationMatrix[strategy1.modelId]?.[strategy2.modelId];

    if (correlation !== undefined) {
      return correlation;
    }

    // Try reverse lookup (matrix might be symmetric)
    const reverseCorrelation =
      correlationMatrix[strategy2.modelId]?.[strategy1.modelId];

    return reverseCorrelation !== undefined ? reverseCorrelation : 0;
  };

  // Prepare data for heatmap
  const prepareHeatmapData = () => {
    const series: Array<{
      name: string;
      data: Array<{
        x: string;
        y: number;
      }>;
    }> = [];

    strategies.forEach((strategy1) => {
      const data = strategies.map((strategy2) => {
        const correlation = getCorrelation(strategy1, strategy2);
        return {
          x: strategy2.name,
          y: Number(correlation) || 0, // Ensure it's always a valid number
        };
      });

      series.push({
        name: strategy1.name,
        data: data,
      });
    });

    console.log('Heatmap series data prepared:', series);
    return series;
  };

  // Heatmap chart options
  const chartOptions: ApexOptions = {
    chart: {
      type: "heatmap",
      toolbar: {
        show: false,
      },
      offsetX: 30,
      offsetY: 0,
    },
    dataLabels: {
      enabled: true,
      style: {
        fontSize: "14px",
        fontWeight: "bold",
        colors: ["#000"],
      },
      formatter: function (val: number) {
        return val.toFixed(3);
      },
    },
    colors: ["#ff2222"],
    plotOptions: {
      heatmap: {
        shadeIntensity: 0.5,
        enableShades: false,
        radius: 12,
        colorScale: {
          ranges: [
            {
              from: -1.01,
              to: -0.8,
              color: "#ff2222",
              name: "-1.0 to -0.8",
            },
            {
              from: -0.8,
              to: -0.6,
              color: "#ff4444",
              name: "-0.8 to -0.6",
            },
            {
              from: -0.6,
              to: -0.4,
              color: "#ff6666",
              name: "-0.6 to -0.4",
            },
            {
              from: -0.4,
              to: -0.2,
              color: "#ff8888",
              name: "-0.4 to -0.2",
            },
            {
              from: -0.2,
              to: 0,
              color: "#ffaaaa",
              name: "-0.2 to 0",
            },
            {
              from: 0,
              to: 0.2,
              color: "#ccdd55",
              name: "0 to 0.2",
            },
            {
              from: 0.2,
              to: 0.4,
              color: "#bbee44",
              name: "0.2 to 0.4",
            },
            {
              from: 0.4,
              to: 0.6,
              color: "#aaff22",
              name: "0.4 to 0.6",
            },
            {
              from: 0.6,
              to: 0.8,
              color: "#aaff11",
              name: "0.6 to 0.8",
            },
            {
              from: 0.8,
              to: 1.01,
              color: "#008000",
              name: "0.8 to 1.0",
            },
          ],
        },
      },
    },
    xaxis: {
      title: {
        text: "Strategies",
        style: {
          fontSize: "14px",
          fontWeight: "bold",
        },
      },
      labels: {
        rotate: -45,
        style: {
          fontSize: "12px",
        },
        maxHeight: 80,
        formatter: function (val: string) {
          // Truncate long strategy names and add ellipsis
          return val.length > 15 ? val.substring(0, 12) + "..." : val;
        },
      },
    },
    yaxis: {
      labels: {
        style: {
          fontSize: "12px",
        },
        offsetX: 0,
      },
    },
    grid: {
      show: false,
    },
    legend: {
      show: true,
      position: "bottom",
      horizontalAlign: "center",
      fontSize: "11px",
      markers: {
        size: 12,
        shape: "square",
      },
      itemMargin: {
        horizontal: 8,
        vertical: 4,
      },
      onItemClick: {
        toggleDataSeries: false,
      },
    },
    tooltip: {
      custom: function ({ series, seriesIndex, dataPointIndex, w }) {
        const strategy1 = w.globals.seriesNames[seriesIndex];
        const strategy2 = strategies[dataPointIndex]?.name || "Unknown"; // Use strategy name
        const correlation = series[seriesIndex][dataPointIndex];

        return `
          <div class="bg-white p-3 border border-gray-200 rounded shadow-lg">
            <div class="font-semibold text-gray-900">${strategy1} vs ${strategy2}</div>
            <div class="text-sm text-gray-600">Correlation: <span class="font-medium">${correlation}</span></div>
          </div>
        `;
      },
    },
  };

  const { tUI } = useTranslation();

  return (
    <div
      className={`bg-white rounded-lg shadow-sm border border-gray-200 p-6 w-full max-w-full overflow-hidden relative ${className}`}
    >
      {/* Strategy Header */}
      <div className="flex items-start justify-between mb-6 pb-6">
        <div className="flex-1">
          <div className="mb-2">
            <h2 className="text-xl font-bold text-gray-900">
              {tUI("correlation.title")}
            </h2>
          </div>
        </div>
      </div>

      {/* Scroll Overlay */}
      {showScrollOverlay && (
        <div className="absolute inset-0 bg-black/50 rounded-lg flex items-center justify-center z-50 transition-opacity duration-500">
          <div className="bg-white rounded-lg shadow-lg p-4 border border-gray-200 max-w-xs mx-4">
            <div className="flex items-center space-x-2 text-blue-600 mb-2">
              <HiArrowRight size={16} />
              <span className="font-medium text-sm">
                {tUI("correlation.scrollHint")}
              </span>
            </div>
            <p className="text-xs text-gray-600">
              {tUI("correlation.scrollBody")}
            </p>
            <div className="flex items-center justify-center mt-4">
              <Button onClick={() => setShowScrollOverlay(false)}>
                <span>{tUI("correlation.understand")}</span>
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Heatmap */}
      <div
        ref={scrollContainerRef}
        className="w-full overflow-x-auto overflow-y-hidden flex justify-center px-4"
      >
        <div
          style={{
            minWidth: "200px",
          }}
          className="flex justify-center flex-col items-center"
        >
          {!isCorrelationDataComplete() && correlationMatrix && (
            <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800 max-w-2xl">
              {tUI("correlation.incompleteDataWarning")}
            </div>
          )}
          {isLoading ? (
            <div className="flex justify-center items-center py-8">
              <div className="animate-pulse">
                <div className="h-96 bg-gray-200 rounded w-96"></div>
              </div>
            </div>
          ) : strategies.length > 0 ? (
            <div className="flex justify-center items-center">
              <Chart
                options={chartOptions}
                series={prepareHeatmapData()}
                type="heatmap"
                height={Math.max(550, strategies.length * 50)}
                width={Math.max(100, strategies.length * 150)}
              />
            </div>
          ) : (
            <div className="text-center text-gray-500 py-8">
              No portfolios available for correlation analysis
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StrategiesCorrelationMatrix;
